import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import _ from 'lodash';

import { ProcessorValueHelperService } from './processor-value-helper.service';
import { SmartenitCurrencyService } from './smartenit-currency.service';
import { TemperatureConverterService } from './temperature-converter.service';
import { SmartenitTranslationService } from './translation/smartenit-translation.service';
import { ActionModel } from '../models/action.model';
import { ConditionModel } from '../models/condition.model';
import { EffectModel } from '../models/effect.model';
import { ControllerModel } from '../models/controller.model';
import { ResponderModel } from '../models/responder.model';
import { DeviceModel } from '../models/device.model';
import {ColorUtils} from '../plugins/color-control-server/color.utils';
import {
  ColorControlServerHueSaturationMode,
  ColorControlServerTemperatureMode, ColorControlServerXYMode
} from '../plugins/color-control-server/modes/index';
import {IColorControlServerMode} from '../plugins/color-control-server/modes/color-control-server-mode.interface';
import { WH_TYPES, WATER_HEATER_MODES } from '../plugins/water-heater-server/water-heater-server.plugin';
@Injectable()
export class NaturalLanguageService {
  constructor(
    private processorValueHelperService: ProcessorValueHelperService,
    private currencyService: SmartenitCurrencyService,
    private temperatureConverterService: TemperatureConverterService,
    private translationService: SmartenitTranslationService
  ) {}

  private translate(key: string): string {
    return this.translationService.performTranslation(key);
  }

  private append(desc: string, value: string, connector?: string) {
    if (value.toString().length > 0) {
      const sep =
        desc.length > 0 ? (connector ? ' ' + connector + ' ' : ' ') : '';
      return desc + sep + value;
    }

    return desc;
  }

  private plural(value: number) {
    if (this.isNumber(value)) {
      return value > 1 ? 's' : '';
    }
    return '';
  }

  private parseOffset(offset: number) {
    const desc = '';
    if (this.isNumber(offset) && offset != 0) {
      return (
        this.parseSeconds(Math.abs(offset)) +
        (offset > 0
          ? ` ${this.translate('GENERAL.AFTER')}`
          : ` ${this.translate('GENERAL.BEFORE')}`)
      );
    }

    return desc;
  }

  private pluralText(text: string, val: number) {
    if (val > 1) {
      return text + 's';
    }

    return text;
  }

  private parseSeconds(secs: number) {
    let desc = '';

    if (secs < 60) {
      desc =
        secs +
        ' ' +
        this.pluralText(this.translate('TIME.FRACTIONS.SEC'), secs);
    } else if (secs >= 60) {
      const remainingSecs = secs % 60;
      const minutes = Math.floor(secs / 60);
      const remainingMinutes = minutes % 60;
      const hours = Math.floor(minutes / 60);

      desc += this.digits(remainingSecs);

      if (hours == 0) {
        if (remainingMinutes > 0 && remainingSecs > 0) {
          return (
            remainingMinutes +
            ':' +
            this.digits(remainingSecs) +
            ' ' +
            this.pluralText(this.translate('TIME.FRACTIONS.MIN'), minutes)
          );
        } else if (remainingMinutes > 0 && remainingSecs == 0) {
          return (
            remainingMinutes +
            ' ' +
            this.pluralText(this.translate('TIME.FRACTIONS.MIN'), minutes)
          );
        } else {
          return (
            remainingSecs +
            ' ' +
            this.pluralText(this.translate('TIME.FRACTIONS.SEC'), remainingSecs)
          );
        }
      } else {
        if (remainingMinutes == 0 && remainingSecs == 0) {
          return (
            hours +
            ' ' +
            this.pluralText(this.translate('TIME.FRACTIONS.HOUR'), hours)
          );
        } else {
          return (
            hours +
            ':' +
            this.digits(remainingMinutes) +
            ':' +
            this.digits(remainingSecs) +
            ' ' +
            this.pluralText(this.translate('TIME.FRACTIONS.HOUR'), hours)
          );
        }
      }
    }

    return desc;
  }

  private appendText(arr: any) {
    if (arr.length == 1) {
      return arr[0];
    }

    const last = arr.pop();
    return arr.join(', ') + ` ${this.translate('GENERAL.AND')} ` + last;
  }

  private rangeToArray(range: string) {
    let res: any = null;
    let sp: any;
    let i: any;

    if (range.indexOf('-') >= 0) {
      sp = range.split('-');
      if (sp.length > 1) {
        res = [];
        for (i = parseInt(sp[0]); i <= parseInt(sp[1]); i++) {
          res.push(i);
        }
      }
    } else if (range.indexOf(',') >= 0) {
      sp = range.split(',');
      if (sp.length > 0) {
        res = [];
        for (i = 0; i < sp.length; i++) {
          res.push(parseInt(sp[i]));
        }
      }
    } else if (this.isNumber(range)) {
      res = [parseInt(range)];
    }

    return res;
  }

  private weekString(week: string) {
    let desc = '';

    if (week == '1-5' || week == '1,2,3,4,5') {
      desc = this.translate('TIME.PERIODS.ON_WEEKDAYS');
    } else if (
      week == '0-6' ||
      week == '0,1,2,3,4,5,6' ||
      week == '1,2,3,4,5,6,7' ||
      week == '1-7' ||
      week == '1,2,3,4,5,6,0'
    ) {
      desc = this.translate('TIME.PERIODS.EVERY_DAY');
    } else if (week == '6-7' || week == '0,6' || week == '6,7') {
      desc = this.translate('TIME.PERIODS.ON_WEEKENDS');
    } else {
      const daysInWeek = [
        this.translate('TIME.DAYS.SUN'),
        this.translate('TIME.DAYS.MON'),
        this.translate('TIME.DAYS.TUE'),
        this.translate('TIME.DAYS.WED'),
        this.translate('TIME.DAYS.THU'),
        this.translate('TIME.DAYS.FRI'),
        this.translate('TIME.DAYS.SAT'),
        this.translate('TIME.DAYS.SUN')
      ];
      const arr = this.rangeToArray(week);

      if (arr) {
        desc = this.append(desc, this.translate('GENERAL.ON'));

        const selectedDays: any = [];
        for (let i = 0; i < arr.length; i++) {
          if (arr[i] < daysInWeek.length) {
            selectedDays.push(daysInWeek[arr[i]]);
          }
        }

        desc = this.append(desc, this.appendText(selectedDays));
      }
    }

    return desc;
  }

  private isNumber(n: any) {
    return !isNaN(parseFloat(n)) && isFinite(n);
  }

  private digits(n: number) {
    return n < 10 ? '0' + n : n;
  }

  private prepend(desc: string, value: string, connector?: string) {
    const sep =
      desc.length > 0 ? (connector ? ' ' + connector + ' ' : ' ') : '';
    return value + sep + desc;
  }

  public ordinal(number: number) {
    let desc = '';

    if (number % 10 == 1) {
      desc = this.translate('ORDINALS.FIRST');
    } else if (number % 10 == 2) {
      desc = this.translate('ORDINALS.SECOND');
    } else if (number % 10 == 3) {
      desc = this.translate('ORDINALS.THIRD');
    } else {
      desc = this.translate('ORDINALS.N');
    }

    return number + desc;
  }

  private color(colorName: string) {
    const key = colorName.toUpperCase().split(' ').join('');
    const value = this.translate('COLORS.' + key);

    return value || colorName;
  }

  private monthString(monthIndex: number) {
    const mL = [
      this.translate('TIME.MONTHS.JANUARY'),
      this.translate('TIME.MONTHS.FEBRUARY'),
      this.translate('TIME.MONTHS.MARCH'),
      this.translate('TIME.MONTHS.APRIL'),
      this.translate('TIME.MONTHS.MAY'),
      this.translate('TIME.MONTHS.JUNE'),
      this.translate('TIME.MONTHS.JULY'),
      this.translate('TIME.MONTHS.AUGUST'),
      this.translate('TIME.MONTHS.SEPTEMBER'),
      this.translate('TIME.MONTHS.OCTOBER'),
      this.translate('TIME.MONTHS.NOVEMBER'),
      this.translate('TIME.MONTHS.DECEMBER')
    ];

    return mL[monthIndex];
  }

  private bitmaskToString(bitmask: number) {
    return (bitmask >>> 0).toString(2);
  }

  private getConnectorByType(type: any) {
    type = type.toLowerCase();

    const isDetects =
      type.indexOf('motion') > -1 ||
      type.indexOf('leak') > -1 ||
      type.indexOf('smoke') > -1 ||
      type.indexOf('fire') > -1 ||
      type.indexOf('water') > -1 ||
      type.indexOf('vibration') > -1 ||
      type.indexOf('carbon') > -1 ||
      type.indexOf('glass') > -1;

    const isIs =
      type.indexOf('contact') > -1 ||
      type.indexOf('remote') > -1 ||
      type.indexOf('key fob') > -1 ||
      type.indexOf('keypad') > -1 ||
      type.indexOf('personal') > -1 ||
      type.indexOf('standard warning') > -1;

    if (isDetects) {
      return this.translate('AR.CONNECTORS.DETECTS');
    } else if (isIs) {
      return this.translate('AR.CONNECTORS.IS');
    }

    return this.translate('AR.CONNECTORS.MATCHES');
  }

  private getDeviceStateFromType(type: string, isActive: boolean) {
    type = type.toLowerCase();

    if (type.indexOf('motion') > -1) {
      return isActive
        ? this.translate('AR.STATES.MOTION')
        : this.translate('AR.STATES.NO_MOTION');
    } else if (type.indexOf('leak') > -1) {
      return isActive
        ? this.translate('AR.STATES.LEAK')
        : this.translate('AR.STATES.NO_LEAK');
    } else if (type.indexOf('smoke') > -1) {
      return isActive
        ? this.translate('AR.STATES.SMOKE')
        : this.translate('AR.STATES.NO_SMOKE');
    } else if (type.indexOf('fire') > -1) {
      return isActive
        ? this.translate('AR.STATES.FIRE')
        : this.translate('AR.STATES.NO_FIRE');
    } else if (type.indexOf('water') > -1) {
      return isActive
        ? this.translate('AR.STATES.WATER')
        : this.translate('AR.STATES.NO_WATER');
    } else if (type.indexOf('vibration') > -1) {
      return isActive
        ? this.translate('AR.STATES.VIBRATION')
        : this.translate('AR.STATES.NO_VIBRATION');
    } else if (type.indexOf('carbon') > -1) {
      return isActive
        ? this.translate('AR.STATES.CO')
        : this.translate('AR.STATES.NO_CO');
    } else if (type.indexOf('glass') > -1) {
      return isActive
        ? this.translate('AR.STATES.GLASS_BREAK')
        : this.translate('AR.STATES.NO_GLASS_BREAK');
    } else if (type.indexOf('contact') > -1) {
      return isActive
        ? this.translate('AR.STATES.OPEN')
        : this.translate('AR.STATES.CLOSED');
    } else if (type.indexOf('standard warning') > -1) {
      return this.translate('AR.STATES.WARNING');
    } else if (type.indexOf('personal') > -1) {
      return this.translate('AR.STATES.EMERGENCY');
    } else if (type.indexOf('remote') > -1) {
      return this.translate('AR.STATES.EMERGENCY');
    } else if (type.indexOf('key fob') > -1) {
      return this.translate('AR.STATES.EMERGENCY');
    } else if (type.indexOf('keypad') > -1) {
      return this.translate('AR.STATES.EMERGENCY');
    }

    return '';
  }

  private getDeviceIdFromPath(path: string = '') {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On
    return this.getResourceIdFromPath(path, 'devices');
  }

  private getComponentName(item: any) {
    if (item.componentName && item.componentName.length > 0) {
      return item.componentName;
    }
  }

  private getMethodFromPath(pathStr: any, onlyParameter: any) {
    const METHODS_DIR = '/methods/';
    const position = onlyParameter ? 1 : 0;
    const path = pathStr || '';
    const hasMethodsDir = path.indexOf(METHODS_DIR) > -1;
    let method;

    if (hasMethodsDir) {
      method = path.split(METHODS_DIR)[1].split('.')[position];
    }

    if (method) {
      method = method.replace(/([A-Z])/g, ' $1').trim();
    }

    return method;
  }

  private getDeviceName(resource: any) {
    return this.getResourceNameFromParents(resource, 'devices');
  }

  private parseAttributeName(path: string) {
    let attributeName = 'attribute';

    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/attrs/On

    const arr = path.split('/');

    if (arr.length >= 8 && arr[7] == 'attrs') {
      if (arr[8].indexOf('.') >= 0) {
        const sp = arr[8].split('.');
        attributeName = sp[0];
      } else {
        attributeName = arr[8];
      }

      attributeName = attributeName.replace(/([A-Z])/g, ' $1').trim();
    }

    return attributeName;
  }

  private parseAttributeCondition(
    attr: string,
    val: any,
    deviceName: string,
    connector: string,
    componentId: string,
    processorName: string,
    device?: DeviceModel,
    deviceId?: string,
    componentName?: string
  ) {
    let desc = '';
    const attrStr = attr.toLowerCase();
    if (attrStr == 'on off') {
      if (parseInt(val) == 1 || val == 'on' || val == true) {
        desc = deviceName + ` ${this.translate('AR.CONDITIONS_NL.IS_ON')}`;
      } else {
        desc = deviceName + ` ${this.translate('AR.CONDITIONS_NL.IS_OFF')}`;
      }
    } else if (attrStr === 'lock state') {
      if (parseInt(val) == 1 || val == 'locked' || val == true) {
        desc = deviceName + ` ${this.translate('AR.CONDITIONS_NL.IS_LOCKED')}`;
      } else {
        desc =
          deviceName + ` ${this.translate('AR.CONDITIONS_NL.IS_UNLOCKED')}`;
      }
    } else if (attrStr == 'current level') {
      val = Math.round(val / 2.55);
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.STATE')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '%';
    } else if (
      attrStr == 'measured value' &&
      processorName === 'RelativeHumidityMeasurementServer'
    ) {
      val = Math.round(val / 100);
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.HUMIDITY')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '%';
    } else if (
      attrStr == 'measured value' &&
      processorName === 'TemperatureMeasurementServer'
    ) {
      val = Math.round(val / 100);
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.TEMPERATURE')
        ) +
        ' ' +
        connector +
        ' ' +
        Math.floor(
          this.temperatureConverterService.convert(
            val,
            TemperatureConverterService.DEFAULT_TEMPERATURE
          )
        ) +
        this.temperatureConverterService.getUnit();
    } else if (
      attrStr == 'measured value' &&
      processorName === 'PressureMeasurementServer'
    ) {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.PRESSURE')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        'kPa';
    } else if (attrStr === 'current summation delivered value') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.ENERGY')
        ) +
        ' ' +
        connector +
        ' ' +
        (val < 1 && val > 0 ? val * 1000 : val ) +
        (val < 1 && val > 0 ? ' Wh' : ' kWh' );
    } else if (attrStr === 'instantaneous demand value') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.POWER')
        ) +
        ' ' +
        connector +
        ' ' +
        (val < 1 && val > 0 ? val * 1000 : val ) +
        (val < 1 && val > 0 ? ' W' : ' kW' );
    } else if (attrStr === 'voltage') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.VOLTAGE')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '-Volts';
    } else if (attrStr === 'current') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.CURRENT')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '-Amps';
    } else if (attrStr === 'frequency') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.FREQUENCY')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '-Hz';
    } else if (attrStr === 'active power') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.POWER')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '-Watts';
    } else if (attrStr === 'battery percentage remaining') {
      val = Math.round(val / 2.55);
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.BATTERY')
        ) +
        ' ' +
        connector +
        ' ' +
        val +
        '%';
    } else if (attrStr === 'local temperature') {
      val = Math.round(val / 100);
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.TEMPERATURE')
        ) +
        ' ' +
        connector +
        ' ' +
        Math.floor(
          this.temperatureConverterService.convert(
            val,
            TemperatureConverterService.DEFAULT_TEMPERATURE
          )
        ) +
        this.temperatureConverterService.getUnit();
    } else if (attrStr === 'period cost') {
      const energyConnector = connector
        .replace('is above', 'exceeds')
        .replace('is below', 'is under');
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.USAGE')
        ) +
        ' ' +
        energyConnector +
        ` ${this.currencyService.currency}` +
        val;
    } else if (attrStr === 'period summation') {
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.ENERGY_CONSUMPTION')
        ) +
        ' ' +
        connector +
        ' ' +
        (val < 1 && val > 0 ? val * 1000 : val ) +
        (val < 1 && val > 0 ? ' Wh' : ' kWh' );
    } else if (attrStr === 'system mode') {
      const idx = parseInt(val);
      let modes = [
        this.translate('AR.CONDITIONS_NL.OFF'),
        device && device.model === 'nest-thermostat'
          ? this.translate('AR.CONDITIONS_NL.HEAT_COOL')
          : this.translate('AR.CONDITIONS_NL.AUTO'),
        undefined,
        this.translate('AR.CONDITIONS_NL.COOL'),
        this.translate('AR.CONDITIONS_NL.HEAT')
      ];

      // if the devices is a water heater, change the modes use those from the plugin
      if (device && WH_TYPES.find((whMode) => whMode.type === device.model)) {
        modes = WATER_HEATER_MODES.map((m) => this.translate(m.translation));
      }

      const mode = modes[idx] || '';
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.MODE')
        ) +
        ` ${this.translate('AR.CONNECTORS.IS_SET_TO')} ` +
        mode;
    } else if (attrStr === 'fan mode') {
      const idx = parseInt(val);
      const modes = [
        this.translate('AR.CONDITIONS_NL.OFF'),
        this.translate('AR.CONDITIONS_NL.LOW'),
        this.translate('AR.CONDITIONS_NL.MEDIUM'),
        this.translate('AR.CONDITIONS_NL.HIGH'),
        this.translate('AR.CONDITIONS_NL.ON'),
        this.translate('AR.CONDITIONS_NL.AUTO'),
        this.translate('AR.CONDITIONS_NL.SMART')
      ];
      const mode = modes[idx] || '';
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.FAN')
        ) +
        ` ${this.translate('AR.CONNECTORS.IS_SET_TO')} ` +
        mode;
    } else if (attrStr === 'zone status') {
      if (val === 'emergency') {
        desc =
          `${this.translate(
            'AR.CONDITIONS_NL.EMERGENCY_BUTTON'
          )} ${this.translate('AR.CONNECTORS.IS_PRESSED_ON')} ` + deviceName;
      } else if (val === 'warning') {
        desc = deviceName + ` ${this.translate('AR.CONNECTORS.SENDS_ALERT')} `;
      } else {
        desc = deviceName + ' ' + connector + ' ' + val;
      }
    } else if (processorName === 'IASACEServer') {
      const panelStatuses: { [key: number]: string } = {
        0x00: this.translate('DEVICES.IAS.LABEL.DISARMED'),
        0x01: this.translate('DEVICES.IAS.LABEL.ARMED_STAY'),
        0x02: this.translate('DEVICES.IAS.LABEL.ARMED_NIGHT'),
        0x03: this.translate('DEVICES.IAS.LABEL.ARMED_AWAY'),
        0x04: this.translate('DEVICES.IAS.LABEL.EXIT_DELAY'),
        0x05: this.translate('DEVICES.IAS.LABEL.ENTRY_DELAY'),
        0x06: this.translate('DEVICES.IAS.LABEL.NOT_READY_TO_ARM'),
        0x07: this.translate('DEVICES.IAS.LABEL.IN_ALARM'),
        0x08: this.translate('DEVICES.IAS.LABEL.ARMING_STAY'),
        0x09: this.translate('DEVICES.IAS.LABEL.ARMING_NIGHT'),
        0x0a: this.translate('DEVICES.IAS.LABEL.ARMING_AWAY'),
      };
      const panelStatus = panelStatuses[val] || '';
      desc =
        this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONNECTORS.IS')
        ) + ` ${panelStatus}`;
    } else if (processorName === 'EVSEServer') {
      if (attrStr === 'charger session i d') {
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.CHARGING_SESSION')
          ) +
          ' ' +
          connector +
          ' #' +
          val;
      } else if (attrStr === 'battery level') {
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.CHARGE_LEVEL')
          ) +
          ' ' +
          connector +
          ' ' +
          val +
          '%';
      } else if (attrStr === 'charger session summation') {
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.POWER')
          ) +
          ' ' +
          connector +
          ' ' +
          (val < 1 && val > 0 ? val * 1000 : val ) +
          (val < 1 && val > 0 ? ' W' : ' kW' );
      } else if (attrStr === 'charger status') {
        const idx = parseInt(val);
        const chargerStatuses: { [key: number]: string } = {
          0: this.translate('DEVICES.EVSE.STATUS.UNPLUGGED'),
          1: this.translate('DEVICES.EVSE.STATUS.NOT_CHARGING'),
          2: this.translate('DEVICES.EVSE.STATUS.READY'),
          3: this.translate('DEVICES.EVSE.STATUS.CHARGING'),
          4: this.translate('DEVICES.EVSE.STATUS.FAULT'),
          5: this.translate('DEVICES.EVSE.STATUS.CHARGED')
        };
        const chargerStatus = chargerStatuses[idx] || '';
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.CHARGER_STATUS')
          ) +
          ` ${this.translate('AR.CONNECTORS.IS')} ` +
          chargerStatus;
      }
    } else if (attrStr.indexOf('setpoint') > -1 || attrStr.indexOf('set point') > -1 || attrStr.indexOf('average temperature') > -1) {
      // pending to get the divisor
      if (processorName !== 'WaterHeaterServer') {
        val = Math.round(val / 100);
      }

      if (attrStr.indexOf('heat') > -1) {
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.HEATING_SETPOINT')
          ) +
          ' ' +
          connector +
          ' ' +
          Math.floor(
            this.temperatureConverterService.convert(
              val,
              TemperatureConverterService.DEFAULT_TEMPERATURE
            )
          ) +
          this.temperatureConverterService.getUnit();
      } else {
        desc =
          this.translationService.reorderSentence(
            deviceName,
            this.translate('AR.CONDITIONS_NL.COOLING_SETPOINT')
          ) +
          ' ' +
          connector +
          ' ' +
          Math.floor(
            this.temperatureConverterService.convert(
              val,
              TemperatureConverterService.DEFAULT_TEMPERATURE
            )
          ) +
          this.temperatureConverterService.getUnit();
      }
    } else if (processorName === 'OnOffClient') {
      if (attrStr == 'attribute') {
        if (val == 'on') {
          desc = componentName + ` ${this.translate('GENERAL.ON')} `
            + deviceName + (' ' + this.translate('AR.CONDITIONS_NL.IS_TURNED_ON'));
        } else if (val == 'off') {
          desc = componentName + ` ${this.translate('GENERAL.ON')} `
            + deviceName + (' ' + this.translate('AR.CONDITIONS_NL.IS_TURNED_OFF'));
        } else {
          desc =
            `${this.translate('AR.CONDITIONS_NL.BUTTON')} ` +
            val +
            ` ${this.translate('AR.CONNECTORS.IS_PRESSED_ON')} ` +
            deviceName;
        }
      } else {
        desc =
          `${this.translate('AR.CONDITIONS_NL.BUTTON')} ` +
          val +
          ` ${this.translate('AR.CONNECTORS.IS_PRESSED_ON')} ` +
          deviceName;
      }
    } else if (processorName === 'IASWDClient') {
      desc =
        `${this.translate('AR.CONDITIONS_NL.BUTTON')} ${this.translate(
          'AR.CONNECTORS.IS_PRESSED_ON'
        )} ` + deviceName;
    } else if (processorName === 'BinaryInput') {
      const inputs: any[] =
        device &&
          device.meta &&
          device.meta.inputs &&
          Array.isArray(device.meta.inputs)
          ? device.meta.inputs
          : [];
      const input: any = inputs.find(
        (i: any) =>
          i.componentId === componentId && i.processor === processorName
      );
      const reversePolarity = input ? !!input.reversePolarity : false;
      deviceName = this.getInputDeviceName(device, input);
      if (!!val) {
        desc =
          deviceName +
          ` ${this.translate(
            `AR.CONDITIONS_NL.${
            !reversePolarity ? 'IS_ALARMED' : 'IS_NOT_ALARMED'
            }`
          )}`;
      } else {
        desc =
          deviceName +
          ` ${this.translate(
            `AR.CONDITIONS_NL.${
            !reversePolarity ? 'IS_NOT_ALARMED' : 'IS_ALARMED'
            }`
          )}`;
      }
    } else if (processorName === 'AnalogInput') {
      val = Math.round( val * 100 / 2.4);
      const inputs: any[] =
        device &&
          device.meta &&
          device.meta.inputs &&
          Array.isArray(device.meta.inputs)
          ? device.meta.inputs
          : [];
      const input: any = inputs.find(
        (i: any) =>
          i.componentId === componentId && i.processor === processorName
      );
      deviceName = this.getInputDeviceName(device, input);
      desc = `${deviceName} ${connector} ${val}${(input && input.units != null) ? input.units : '%'}`;
    } else if (processorName == 'OccupancySensingServer') {
      if (parseInt(val) == 1 || val == 'on' || val == true) {
        desc = deviceName + ` ${this.translate('AR.CONDITIONS.STATUS')}` + ` ${this.translate('AR.CONDITIONS_NL.IS_OCCUPIED')}`;
      } else {
        desc = deviceName + ` ${this.translate('AR.CONDITIONS.STATUS')}` + ` ${this.translate('AR.CONDITIONS_NL.IS_UNOCCUPIED')}`;
      }
    } else {
      desc =
        attr +
        ` ${this.translate('GENERAL.IN')} ` +
        deviceName +
        ' ' +
        connector +
        ' ' +
        val;
    }

    return desc;
  }

  private getMultipleComponentsName(
    device: any,
    deviceId: any,
    processorName: any,
    componentId: any,
    parents: any
  ) {
    let deviceName = '';

    if (device) {
      deviceName = device.name
        ? device.name
        : `${this.translate('GENERAL.DEVICE')} ` + device._id;

      if (device.components) {
        const components = [];

        for (let i = 0; i < device.components.length; i++) {
          const component = device.components[i];
          if (component && component.processors) {
            for (let j = 0; j < component.processors.length; j++) {
              const processor = component.processors[j];

              if (processor && processor.name == processorName && processorName !== 'OnOffClient') {
                components.push(component);
              }
            }
          }
        }

        if (components.length > 1) {
          const component = components.find((it) => {
            return it.id == componentId;
          });

          if (component) {
            deviceName = deviceName + ' ' + component.name;
          }
        }
      }
    } else if (parents && parents.devices) {
      const parentDevice = parents.devices.find((item: any) => {
        return deviceId == item.id;
      });

      if (parentDevice && parentDevice.name) {
        deviceName = parentDevice.name;
      } else {
        deviceName = `${this.translate('GENERAL.DEVICE')} ` + deviceId;
      }
    } else if (deviceId) {
      deviceName = `${this.translate('GENERAL.DEVICE')} ` + deviceId;
    }

    return deviceName;
  }

  private setDeviceConditionDescription(
    condition: any,
    connector: string,
    val: string,
    device?: DeviceModel
  ) {
    let description = '';

    if (
      condition.path &&
      (condition.type == 'device-state' ||
        condition.type == 'energy-management')
    ) {
      const resName =
        condition.type == 'device-state'
          ? 'devices'
          : this.getResourceNameFromPath(condition.path);
      let attr = this.parseAttributeName(condition.path);
      const methodParam = this.getMethodFromPath(condition.path, true);
      const resId = this.getResourceIdFromPath(condition.path, resName);
      const processorName = this.getProcessorFromPath(condition.path, resName);
      const componentId = this.getComponentFromPath(condition.path, resName);
      const componentName = condition.componentName ||
        this.translate('AR.CONDITIONS_NL.BUTTON').toLowerCase();

      attr = attr === 'attribute' && methodParam ? methodParam : attr;

      if (resId) {
        let resourceName;
        if (condition.type == 'energy-management') {
          resourceName = this.getResourceNameFromParents(condition, resName);
        } else {
          resourceName = this.getMultipleComponentsName(
            device,
            resId,
            processorName,
            componentId,
            condition.parents
          );
        }

        const supportedConnector =
          this.translate('AR.CONNECTORS.IS_EQUAL_TO') === connector ||
          this.translate('AR.CONNECTORS.IS_ABOVE') === connector ||
          this.translate('AR.CONNECTORS.IS_BELOW') === connector ||
          this.translate('AR.CONNECTORS.IS') === connector ||
          this.translate('AR.CONNECTORS.DETECTS') === connector;

        if (supportedConnector) {
          description = this.append(
            description,
            `${this.translate('GENERAL.WHEN')} ` +
            this.parseAttributeCondition(
              attr,
              val,
              resourceName,
              connector,
              componentId,
              processorName,
              device,
              resId,
              componentName
            )
          );
        } else {
          description = this.append(
            description,
            `${this.translate('GENERAL.WHEN')} ` +
            attr +
            ` ${this.translate('GENERAL.IN')} ` +
            resourceName +
            ' ' +
            connector +
            ' ' +
            val
          );
        }
      }
    }

    return description;
  }

  private parseCrontab(
    crontab: string,
    sunTime?: boolean,
    timezone?: string,
    hasFromTo?: boolean
  ) {
    let desc = '';
    const parts = crontab.split(' ');

    if (parts.length >= 5) {
      let minutes = <any> parts[0];
      const hours = <any> parts[1];
      const date = parts[2];
      const month = parts[3];
      const week = parts[4];

      const offsetTime = this.calcTimezone(timezone);
      let innerDate: any;

      if (date == '*' && week == '*') {
        if (minutes != '*' || hours != '*') {
          const { hrsUtc, minsUtc } = this.convertHoursMinutesToUtc(
            hours,
            minutes,
            offsetTime
          );
          const hrs = this.getRegularHourFormat(hrsUtc);
          const amPm = this.getAmPmByHour(hrsUtc);
          desc = this.append(
            desc,
            `${this.translate('TIME.PERIODS.EVERY_DAY')} ${this.translate(
              'TIME.PERIODS.AT_HOUR'
            )} ` +
            hrs +
            ':' +
            this.digits(minsUtc) +
            ' ' +
            amPm
          );

          if (month != '*') {
            desc = this.append(
              desc,
              `${this.translate('GENERAL.DURING')} ` +
              this.monthString(parseInt(month))
            );
          }

          return desc;
        }
      }

      if (!sunTime) {
        if (minutes != '*' && hours != '*' && !hasFromTo) {
          const { hrsUtc, minsUtc } = this.convertHoursMinutesToUtc(
            hours,
            minutes,
            offsetTime
          );
          const hrs = this.getRegularHourFormat(hrsUtc);
          const amPm = this.getAmPmByHour(hrsUtc);

          if (parseInt(hours) != 0) {
            desc = this.append(
              desc,
              `${this.translate('TIME.PERIODS.AT_HOUR')} ` +
              hrs +
              ':' +
              this.digits(minsUtc) +
              ' ' +
              amPm
            );
          }
        } else if (hours == '*' && minutes == '0' && !hasFromTo) {
          desc =
            desc.length > 0
              ? this.append(desc, this.translate('TIME.PERIODS.EVERY_HOUR'))
              : this.append(desc, this.translate('TIME.PERIODS.HOURLY'));
        } else if (hours == '*' && minutes != '*' && !hasFromTo) {
          innerDate = new Date(parseInt(minutes) * 60 * 1000);
          innerDate.setTime(innerDate.getTime() + offsetTime);

          minutes = innerDate.getUTCMinutes();

          desc = this.append(
            desc,
            `${this.translate('TIME.PERIODS.EVERY')} ` +
            (parseInt(minutes) != 1 ? minutes + ' ' : '') +
            this.pluralText(
              this.translate('TIME.FRACTIONS.MINUTE'),
              parseInt(minutes)
            )
          );
        }
      }

      if (week != '*' && date.indexOf('/2') < 0) {
        const weekStr = this.weekString(week);
        desc = this.prepend(desc, weekStr);
      } else if (week === '*' && date.indexOf('/2') < 0) {
        desc = this.prepend(desc, this.translate('TIME.PERIODS.EVERY_DAY'));
      }

      if (date === '1-31/2') {
        desc = this.prepend(
          desc,
          `${this.translate('TIME.PERIODS.ON_ODD_DATES')}, `
        );
      } else if (date === '0-30/2') {
        desc = this.prepend(
          desc,
          `${this.translate('TIME.PERIODS.ON_EVEN_DATES')}, `
        );
      } else if (date !== '*') {
        desc = this.append(
          desc,
          `${this.translate('GENERAL.EACH')} ` +
          this.ordinal(parseInt(date)) +
          (month == '*'
            ? ` ${this.translate('TIME.PERIODS.DAY_OF_THE_MONTH')}`
            : '')
        );
      }

      if (month != '*') {
        desc = this.append(
          desc,
          `${this.translate('GENERAL.DURING')} ` +
          this.monthString(parseInt(month))
        );
      }
    }

    return desc;
  }

  private convertHoursMinutesToUtc(hours: any, minutes: any, offsetTime: any) {
    const innerDate = new Date(
      (parseInt(hours) * 60 * 60 + parseInt(minutes) * 60) * 1000
    );
    innerDate.setTime(innerDate.getTime() + offsetTime);
    const hrsUtc = innerDate.getUTCHours();

    return {
      hrsUtc: innerDate.getUTCHours(),
      minsUtc: innerDate.getUTCMinutes()
    };
  }

  private getRegularHourFormat(hourUtc: number) {
    if (hourUtc >= 12) {
      hourUtc = hourUtc - 12;
    }
    if (hourUtc === 0) {
      hourUtc = 12;
    }
    return hourUtc;
  }

  private getAmPmByHour(hour: number) {
    let dd = 'AM';
    if (hour >= 12) {
      dd = 'PM';
    }
    return dd;
  }

  private parseDelay(delay: number) {
    if (this.isNumber(delay)) {
      if (delay < 60) {
        return delay + ` ${this.translate('TIME.FRACTIONS.SECS')}`;
      } else if (delay >= 60) {
        return this.parseSeconds(delay);
      }
    }
  }

  private getDeviceMethodFromPath(path: string) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    return this.extractMethodFromPath(path, 'devices');
  }

  private getDeviceAttributeFromPath(path: string) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    return this.getAttributeFromPath(path, 'devices');
  }

  private parseDuration(duration: any) {
    // console.log("Duration: ",duration);
    const hours: number = Math.floor(duration / 3600);
    duration %= 3600;
    const minutes: number = Math.floor(duration / 60);
    const seconds: number = duration % 60;
    let desc: string = ` ${this.translate('GENERAL.FOR')} `;

    if (hours > 0) {
      desc +=
        String(hours) +
        (hours > 1
          ? ` ${this.translate('TIME.FRACTIONS.HRS')}`
          : ` ${this.translate('TIME.FRACTIONS.HR')}`);
    }
    if (minutes > 0) {
      desc +=
        (hours > 0 ? ' ' : '') +
        String(minutes) +
        (minutes > 1
          ? ` ${this.translate('TIME.FRACTIONS.MINS')}`
          : ` ${this.translate('TIME.FRACTIONS.MIN')}`);
    }
    if (seconds > 0) {
      if (hours > 0 || minutes > 0) {
        desc += ' ';
      }
      desc +=
        String(seconds) +
        (seconds > 1
          ? ` ${this.translate('TIME.FRACTIONS.SECS')}`
          : ` ${this.translate('TIME.FRACTIONS.SEC')}`);
    }
    if (seconds == 0 && hours == 0 && minutes == 0) {
      desc = '';
    }

    return desc;
  }

  private parseMethod(method: string, deviceName: string, value: any) {
    let desc: any;

    const lowerCaseMethod = method.toLowerCase();

    if (
      lowerCaseMethod.indexOf('on') == 0 ||
      lowerCaseMethod.indexOf('off') == 0 ||
      lowerCaseMethod.indexOf('programon') == 0 ||
      lowerCaseMethod.indexOf('programoff') == 0
    ) {
      const action = method.replace('Program', '').toLowerCase();
      desc =
        (action === 'on'
          ? `${this.translate('AR.EFFECTS_NL.TURN_ON')} `
          : `${this.translate('AR.EFFECTS_NL.TURN_OFF')} `) + deviceName;
    } else if (lowerCaseMethod.indexOf('toggle') == 0) {
      desc = this.translate('AR.EFFECTS_NL.TOGGLE') + ' ' + deviceName;
    } else if (
      lowerCaseMethod.indexOf('movetolevel') == 0 &&
      value.value != null
    ) {
      desc =
        `${this.translate('AR.EFFECTS_NL.SET')} ` +
        deviceName +
        ` ${this.translate('GENERAL.TO')} ` +
        value.value +
        '%';
    } else if (lowerCaseMethod.indexOf('unlockdoor') == 0) {
      desc = `${this.translate('AR.EFFECTS_NL.UNLOCK')} ` + deviceName;
    } else if (lowerCaseMethod.indexOf('lockdoor') == 0) {
      desc = `${this.translate('AR.EFFECTS_NL.LOCK')} ` + deviceName;
    } else if (lowerCaseMethod.indexOf('startwarning') == 0) {
      // console.log("start warning value is:", value);
      if (value.WarningMode == 0) {
        desc =
          `${this.translate('AR.EFFECTS_NL.STOP_WARNING_ON')} ` + deviceName;
      } else {
        let WarningMeaning = '';
        switch (value.WarningMode) {
          case '0xF': {
            WarningMeaning = this.translate(
              'AR.CONDITIONS_NL.WARNING_MODE.BELL'
            );
            break;
          }
          case '0x1': {
            WarningMeaning = this.translate(
              'AR.CONDITIONS_NL.WARNING_MODE.BURGLAR'
            );
            break;
          }
          case '0x2': {
            WarningMeaning = this.translate(
              'AR.CONDITIONS_NL.WARNING_MODE.FIRE'
            );
            break;
          }
          case '0x3': {
            WarningMeaning = this.translate(
              'AR.CONDITIONS_NL.WARNING_MODE.PANIC'
            );
            break;
          }
          default: {
            WarningMeaning = this.translate(
              'AR.CONDITIONS_NL.WARNING_MODE.BELL'
            );
            break;
          }
        }

        desc =
          `${this.translate('AR.EFFECTS_NL.START_THE')} ` +
          WarningMeaning +
          ` ${this.translate('AR.EFFECTS_NL.SOUND_ON')} ` +
          deviceName +
          this.parseDuration(value.Duration);
      }
    } else if (lowerCaseMethod.indexOf('sendemail') == 0) {
      desc = this.translate('AR.EFFECTS_NL.SEND_EMAIL');
    } else if (lowerCaseMethod.indexOf('notification') > -1) {
      desc = this.translate('AR.EFFECTS_NL.SEND_PUSH_NOTIFICATION');
    } else if (
      lowerCaseMethod.indexOf('movetohueandsaturation') === 0 ||
      lowerCaseMethod.indexOf('movetocolortemperature') === 0 ||
      lowerCaseMethod.indexOf('movetocolor') === 0
    ) {
      const color = this.getColorFromColorControlPayload(lowerCaseMethod, value)
        .replace(/ /g, '').toUpperCase();
      desc =
        `${this.translate('AR.EFFECTS_NL.SET')} ` +
        `${this.translationService.reorderSentence(
          deviceName,
          this.translate('GENERAL.COLOR')
        )} ${this.translate('GENERAL.TO')} ` +
        `${this.translate('COLORS.' + color)}`;
    } else if (lowerCaseMethod.indexOf('startcharge') == 0) {
      desc = `${this.translate(
        'AR.EFFECTS_NL.START_CHARGING_SESSION_IN'
      )} ${deviceName}`;
    } else if (lowerCaseMethod.indexOf('stopcharge') == 0) {
      desc = `${this.translate(
        'AR.EFFECTS_NL.STOP_CHARGING_SESSION_IN'
      )} ${deviceName}`;
    } else {
      desc =
        `${this.translate('AR.EFFECTS_NL.EXECUTE')} ` +
        method +
        ` ${this.translate('GENERAL.ON')} ` +
        deviceName;
    }

    return desc;
  }

  private parseAttribute(
    attribute: string,
    deviceName: string,
    value: any,
    device?: DeviceModel
  ) {
    let desc;
    const valueStr = value && value.value;

    const lowerCaseAttribute = attribute.toLowerCase();

    if (lowerCaseAttribute.indexOf('onoff') == 0) {
      desc =
        (valueStr
          ? `${this.translate('AR.EFFECTS_NL.TURN_ON')} `
          : `${this.translate('AR.EFFECTS_NL.TURN_OFF')} `) + deviceName;
    } else if (lowerCaseAttribute.indexOf('currentlevel') == 0) {
      desc =
        `${this.translate('AR.EFFECTS_NL.SET')} ` +
        deviceName +
        ` ${this.translate('GENERAL.TO')} ` +
        valueStr +
        '%';
    } else if (lowerCaseAttribute.indexOf('systemmode') == 0) {
      // console.log('is system mode');
      const idx = parseInt(valueStr);
      let modes = [
        this.translate('AR.CONDITIONS_NL.OFF'),
        device && device.model === 'nest-thermostat'
          ? this.translate('AR.CONDITIONS_NL.HEAT_COOL')
          : this.translate('AR.CONDITIONS_NL.AUTO'),
        undefined,
        this.translate('AR.CONDITIONS_NL.COOL'),
        this.translate('AR.CONDITIONS_NL.HEAT')
      ];

      // if the devices is a water heater, change the modes use those from the plugin
      if (device && WH_TYPES.find((whMode) => whMode.type === device.model)) {
        modes = WATER_HEATER_MODES.map((m) => this.translate(m.translation));
      }

      const mode = modes[idx] || '';
      desc =
        `${this.translate('AR.EFFECTS_NL.SET')} ` +
        deviceName +
        ` ${this.translate('GENERAL.TO')} ` +
        mode;
    } else if (lowerCaseAttribute.indexOf('setpoint') > -1) {
      // console.log('is set points');
      let attrName = this.translationService.reorderSentence(
        deviceName,
        this.translate('AR.CONDITIONS_NL.SETPOINT')
      );
      if (lowerCaseAttribute.indexOf('heat') > -1) {
        attrName = this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.HEATING_SETPOINT')
        );
      } else {
        attrName = this.translationService.reorderSentence(
          deviceName,
          this.translate('AR.CONDITIONS_NL.COOLING_SETPOINT')
        );
      }
      desc =
          `${this.translate('AR.EFFECTS_NL.SET')} ${attrName} ${this.translate('GENERAL.TO')} ` +
          Math.floor(
            this.temperatureConverterService.convert(
              valueStr,
              TemperatureConverterService.DEFAULT_TEMPERATURE
            )
          ) +
          this.temperatureConverterService.getUnit();
    } else {
      /*else if (lowerCaseAttribute.indexOf('fanmode') == 0 && valueStr) {
      desc = 'set ' + deviceName + ' to ';
      if(valueStr == 0) desc += "off";
      else if(valueStr == 1) desc += "auto";
      else if(valueStr == 3) desc += "cool";
      else if(valueStr == 4) desc += "heat";
    }*/
      desc =
        `${this.translate('AR.EFFECTS_NL.SET')} ` +
        attribute +
        ` ${this.translate('GENERAL.TO')} ` +
        valueStr +
        ` ${this.translate('GENERAL.ON')} ` +
        deviceName;
    }

    return desc;
  }

  public getDateDescription(date: Date, todaysDate: Date) {
    let desc = '';

    if (
      date &&
      date.getHours &&
      date.getMinutes &&
      date.getDate &&
      date.getMonth
    ) {
      desc =
        `${this.translate('TIME.PERIODS.AT_HOUR')} ` +
        date.getHours() +
        ':' +
        this.digits(date.getMinutes());

      if (date.getDate() == todaysDate.getDate()) {
        desc += `, ${this.translate('TIME.PERIODS.TODAY')}`;
      } else {
        desc +=
          ', ' +
          this.monthString(date.getMonth()) +
          ' ' +
          this.ordinal(date.getDate());
      }
    }

    return desc;
  }

  public getConditionDescription(
    condition: ConditionModel,
    device?: DeviceModel
  ) {
    let description = '';

    if (condition && condition.value != undefined) {
      const val = condition.value;
      let parsedEqual = false;

      if (val.hasOwnProperty('#crontab')) {
        const hasFromTo =
          val.hasOwnProperty('#from') || val.hasOwnProperty('#to');

        if (
          val.hasOwnProperty('#eval') &&
          (val['#eval'] == 'sunset' || val['#eval'] == 'sunrise')
        ) {
          parsedEqual = true;

          const evalValue =
            val['#eval'] == 'sunset'
              ? this.translate('TIME.PERIODS.SUNSET')
              : this.translate('TIME.PERIODS.SUNRISE');

          if (val.hasOwnProperty('#offset')) {
            const parsedOffset = this.parseOffset(val['#offset']);
            description = this.append(description, parsedOffset);
            description = this.append(
              description,
              !parsedOffset
                ? `${this.translate('TIME.PERIODS.AT')} ` + evalValue
                : evalValue
            );
          } else {
            description = this.append(
              description,
              `${this.translate('TIME.PERIODS.AT')} ` + evalValue
            );
          }

          description = this.prepend(
            description,
            this.parseCrontab(val['#crontab'], true, val['#tz'], hasFromTo)
          );
          description = description && description.trim();
        } else {
          description = this.append(
            description,
            this.parseCrontab(val['#crontab'], false, val['#tz'], hasFromTo)
          );
        }
      }

      if (
        condition.path &&
        (condition.type == 'device-state' ||
          condition.type == 'energy-management')
      ) {
        const resName =
          condition.type == 'device-state'
            ? 'devices'
            : this.getResourceNameFromPath(condition.path);
        const processorName = this.getProcessorFromPath(
          condition.path,
          resName
        );
        const method = this.getMethodFromPath(condition.path, resName);
        const attribute = this.getAttributeFromPath(condition.path, resName);

        if (typeof val === 'string') {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_EQUAL_TO'),
              val,
              device
            )
          );
        } else if (val.hasOwnProperty('#eq')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_EQUAL_TO'),
              val['#eq'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#gt')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_ABOVE'),
              val['#gt'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#gte')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_GREATER_OR_EQUAL_TO'),
              val['#gte'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#lt')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_BELOW'),
              val['#lt'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#lte')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_LOWER_OR_EQUAL_TO'),
              val['#lte'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#ne')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.IS_DIFFERENT_FROM'),
              val['#ne'],
              device
            )
          );
        }

        if (val.hasOwnProperty('#regex')) {
          description = this.append(
            description,
            this.setDeviceConditionDescription(
              condition,
              this.translate('AR.CONNECTORS.MATCHES'),
              '"' + val['#regex'] + '"',
              device
            )
          );
        }

        if (val.hasOwnProperty('#bitmask')) {
          const type = val['#type'];
          if (type) {
            const isActive = !val.hasOwnProperty('#expected');
            const connector = this.getConnectorByType(type);
            const deviceState = this.getDeviceStateFromType(type, isActive);

            description = this.append(
              description,
              this.setDeviceConditionDescription(
                condition,
                connector,
                deviceState,
                device
              )
            );
          } else {
            description = this.append(
              description,
              this.setDeviceConditionDescription(
                condition,
                this.translate('AR.CONNECTORS.IS_EQUAL_TO'),
                this.bitmaskToString(val['#bitmask']),
                device
              )
            );
          }
        }
      } else {
        if (!parsedEqual && typeof val === 'string') {
          description = this.append(
            description,
            `${this.translate('TIME.PERIODS.AT_HOUR')} ` + val
          );
        }

        if (!parsedEqual && val.hasOwnProperty('#eq')) {
          const equalValue = val['#eq'];
          const timeZone = val['#tz'];

          if (timeZone) {
            const timeValue = this.parseDateVal(
              equalValue,
              timeZone,
              true,
              false
            );
            description = this.append(
              description,
              `${this.translate('TIME.PERIODS.AT_HOUR')} ` + timeValue
            );
          } else {
            description = this.append(
              description,
              `${this.translate('TIME.PERIODS.AT_HOUR')} ` + equalValue
            );
          }
        }

        if (val.hasOwnProperty('#random')) {
          description = this.append(
            description,
            this.translate('AR.CONNECTORS.RANDOMLY')
          );
        }

        if (val.hasOwnProperty('#interval')) {
          description = this.append(
            description,
            this.parseInterval(val['#interval'])
          );
        }

        if (val.hasOwnProperty('#from') && !val.hasOwnProperty('#to')) {
          const fromValueHasDatetime = val['#fromHasDatetime'];
          description = this.append(
            description,
            `${this.translate('AR.TIME.STARTING_ON')} ` +
            this.parseDateVal(
              val['#from'],
              val['#tz'],
              false,
              fromValueHasDatetime
            )
          );
        }

        if (val.hasOwnProperty('#from') && val.hasOwnProperty('#to')) {
          const fromValueHasDatetime = val['#fromHasDatetime'];
          const toValueHasDatetime = val['#toHasDatetime'];
          const timeZone = val['#tz'];
          const fromValue = val['#from'];
          const toValue = val['#to'];
          const fromValueDate = val['#fromDate'];
          const toValueDate = val['#toDate'];
          const fromValueOnlyTime = isNaN(Date.parse(fromValue));
          const toValueOnlyTime = isNaN(Date.parse(toValue));
          const from = this.parseDateVal(
            fromValue,
            timeZone,
            fromValueOnlyTime,
            fromValueHasDatetime,
            fromValueDate,
            toValueDate,
            true
          );
          const to = this.parseDateVal(
            toValue,
            timeZone,
            toValueOnlyTime,
            toValueHasDatetime,
            fromValueDate,
            toValueDate,
            false
          );
          description = this.append(
            description,
            `${this.translate('GENERAL.BETWEEN')} ` +
            from +
            ` ${this.translate('GENERAL.AND')} ` +
            to
          );
        }

        if (val.hasOwnProperty('#period')) {
          description = this.append(
            description,
            `${this.translate('TIME.PERIODS.EVERY')} ` +
            val['#period'] +
            ` ${this.translate('TIME.FRACTIONS.SEC')}` +
            this.plural(val['#period'])
          );
        }

        if (val.hasOwnProperty('#regex')) {
          description = this.append(
            description,
            this.translate('AR.TIME.TIME_MATCHES') + ' "' + val['#regex'] + '"'
          );
        }
      }
    }

    // console.log('[SDK getConditionDescription]', condition);
    return description.charAt(0).toUpperCase() + description.substr(1);
  }

  private calcTimezone(timezone: any) {
    let offsetTime = 0;

    if (timezone && timezone.length > 0) {
      const offset = timezone.split(':');

      if (offset && offset.length > 1) {
        const offsetHours = parseInt(offset[0]);
        const offsetMinutes = parseInt(offset[1]);

        offsetTime = (offsetHours * 60 + offsetMinutes) * 60 * 1000;
      }
    }

    return offsetTime;
  }

  private parseDateVal(
    value: any,
    timeZone: string,
    onlyTime?: boolean,
    dateAndTime?: boolean,
    fromValueDate?: any,
    toValueDate?: any,
    isFromType?: boolean
  ) {
    let date = '';
    const offsetTime = this.calcTimezone(timeZone);

    const parsedDate = Date.parse(value);

    if (isNaN(parsedDate)) {
      if (value.indexOf(':') >= 0) {
        const parsedTime = value.split(':');
        const dateHours = parseInt(parsedTime[0]);
        const dateMinutes = parseInt(parsedTime[1]);

        const innerDate = new Date((dateHours * 60 + dateMinutes) * 60 * 1000);
        innerDate.setTime(innerDate.getTime() + offsetTime);

        const hrsUtc = innerDate.getUTCHours();
        const minsUtc = innerDate.getUTCMinutes();
        const hrs = this.getRegularHourFormat(hrsUtc);
        const amPm = this.getAmPmByHour(hrsUtc);

        date = hrs + ':' + this.digits(minsUtc) + ' ' + amPm;
      } else {
        const dateType = value == 'sunset'
          ? this.translate('TIME.PERIODS.SUNSET')
          : this.translate('TIME.PERIODS.SUNRISE');

        if (toValueDate || fromValueDate) {
          const innerDate = isFromType ? new Date(fromValueDate) : new Date(toValueDate);
          try {
            const monthUtc = innerDate.getUTCMonth();
            const dateUtc = innerDate.getUTCDate();
            const yearUtc = innerDate.getUTCFullYear();
            date =
              this.monthString(monthUtc) +
              ' ' +
              this.ordinal(dateUtc) +
              ' ' +
              yearUtc;
          } catch (err) {
            date = value;
          }
          date = date + (' ' + this.translate('GENERAL.AT_1') + ' ') + dateType;
        } else {
          date = dateType;
        }
      }
    } else {
      const innerDate = new Date(Date.parse(value));
      innerDate.setTime(innerDate.getTime() + offsetTime);

      const hrsUtc = innerDate.getUTCHours();
      const minsUtc = innerDate.getUTCMinutes();
      const hrs = this.getRegularHourFormat(hrsUtc);
      const amPm = this.getAmPmByHour(hrsUtc);

      if (onlyTime) {
        try {
          date = hrs + ':' + this.digits(minsUtc) + ' ' + amPm;
        } catch (err) {
          date = value;
        }
      } else {
        try {
          const monthUtc = innerDate.getUTCMonth();
          const dateUtc = innerDate.getUTCDate();
          const yearUtc = innerDate.getUTCFullYear();
          date =
            this.monthString(monthUtc) +
            ' ' +
            this.ordinal(dateUtc) +
            ' ' +
            yearUtc;
        } catch (err) {
          date = value;
        }
      }

      if (dateAndTime && !isNaN(parsedDate)) {
        date = this.append(
          date,
          ` ${this.translate('TIME.PERIODS.AT_HOUR')} ` +
          hrs +
          ':' +
          this.digits(minsUtc) +
          ' ' +
          amPm
        );
      }
    }

    return date;
  }

  private parseInterval(interval: string) {
    const split = interval.split(' ');
    let desc: string = '';

    if (split && split.length >= 3) {
      const days: any = split[0];
      const hours: any = split[1];
      const minutes: any = split[2];

      let hoursDesc = '';
      let minutesDesc = '';
      let secsDesc = '';

      if (days && days != 0) {
        desc = `${this.translate('AR.TIME.REPEATING_EVERY')} `;
        if (days == 1) {
          hoursDesc = this.translate('TIME.FRACTIONS.DAY');
        } else {
          hoursDesc = days + ` ${this.translate('TIME.FRACTIONS.DAYS')}`;
        }
      }

      if (hours && hours != 0) {
        desc = `${this.translate('AR.TIME.REPEATING_EVERY')} `;
        if (hours == 1) {
          minutesDesc = this.translate('TIME.FRACTIONS.HOUR');
        } else {
          minutesDesc = hours + ` ${this.translate('TIME.FRACTIONS.HOURS')}`;
        }
      }

      if (minutes && minutes != 0) {
        desc = `${this.translate('AR.TIME.REPEATING_EVERY')} `;
        if (minutes == 1) {
          secsDesc = this.translate('TIME.FRACTIONS.MINUTE');
        } else {
          secsDesc = minutes + ` ${this.translate('TIME.FRACTIONS.MINUTES')}`;
        }
      }

      const arr = [hoursDesc, minutesDesc, secsDesc].filter((item) => {
        return item.length > 0;
      });

      if (arr.length === 1) {
        desc += arr[0] + ',';
      } else if (arr.length === 2) {
        desc += arr.join(` ${this.translate('GENERAL.AND')} `) + ',';
      } else if (arr.length === 3) {
        desc +=
          arr[0] +
          ', ' +
          arr[1] +
          ` ${this.translate('GENERAL.AND')} ` +
          arr[2] +
          ',';
      }
    }

    return desc;
  }

  public getEffectDescription(effect: EffectModel, device?: DeviceModel) {
    let desc = '';

    const deviceId = this.getDeviceIdFromPath(effect.path);
    const processorName = this.getDeviceProcessorFromPath(effect.path);
    const componentId = this.getDeviceComponentFromPath(effect.path);

    if (effect.delay) {
      desc = this.append(
        desc,
        `${this.translate('AR.EFFECTS_NL.AFTER')} ` +
        this.parseDelay(effect.delay) +
        ','
      );
    }

    if (deviceId) {
      let deviceName = this.getMultipleComponentsName(
        device,
        deviceId,
        processorName,
        componentId,
        effect.parents
      );
      const valueDesc = this.processorValueHelperService.getValueDescription(
        device,
        componentId,
        processorName,
        effect.params
      );
      deviceName = valueDesc ? deviceName.concat(valueDesc) : deviceName;

      const method = this.getDeviceMethodFromPath(effect.path);
      const attribute = this.getDeviceAttributeFromPath(effect.path);

      if (method) {
        const parsedMethod = this.parseMethod(
          method,
          deviceName,
          effect.params
        );

        desc = this.append(desc, parsedMethod);
      } else if (attribute) {
        const parsedAttribute = this.parseAttribute(
          attribute,
          deviceName,
          effect.params,
          device
        );

        desc = this.append(desc, parsedAttribute);
      } else {
        desc = this.append(
          desc,
          this.translate('AR.EFFECTS_NL.EXECUTE_METHOD')
        );
        desc = this.append(desc, deviceName, this.translate('GENERAL.ON'));
      }
    }

    // console.log('[SDK getEffectDescription]', effect);
    return desc.charAt(0).toUpperCase() + desc.substr(1);
  }

  public getResponderDescription(
    responder: ResponderModel,
    device: DeviceModel
  ) {
    let desc = '';

    const deviceId = this.getDeviceIdFromPath(responder.path);
    const processorName = this.getDeviceProcessorFromPath(responder.path);
    const componentId = this.getDeviceComponentFromPath(responder.path);

    if (deviceId) {
      const deviceName = this.getMultipleComponentsName(
        device,
        deviceId,
        processorName,
        componentId,
        responder.parents
      );

      const method = this.getDeviceMethodFromPath(responder.path);
      const attribute = this.getDeviceAttributeFromPath(responder.path);

      if (method) {
        const parsedMethod = this.parseMethod(
          method,
          deviceName,
          responder.params
        );

        desc = this.append(desc, parsedMethod);
      } else if (attribute) {
        const parsedAttribute = this.parseAttribute(
          attribute,
          deviceName,
          responder.params,
          device
        );

        desc = this.append(desc, parsedAttribute);
      } else {
        desc = this.append(
          desc,
          this.translate('AR.EFFECTS_NL.EXECUTE_METHOD')
        );
        desc = this.append(desc, deviceName, this.translate('GENERAL.ON'));
      }
    }

    return desc.charAt(0).toUpperCase() + desc.substr(1);
  }

  private getDeviceComponentFromPath(path: string) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    return this.getComponentFromPath(path, 'devices');
  }

  private getDeviceProcessorFromPath(path: string) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On
    return this.getProcessorFromPath(path, 'devices');
  }

  public getControllerDescription(
    controller: ControllerModel,
    device: DeviceModel
  ) {
    const desc = '';

    const deviceId = this.getDeviceIdFromPath(controller.path);
    const processorName = this.getDeviceProcessorFromPath(controller.path);
    const componentId = this.getDeviceComponentFromPath(controller.path);

    if (deviceId) {
      const deviceName = this.getMultipleComponentsName(
        device,
        deviceId,
        processorName,
        componentId,
        controller.parents
      );
      const componentName =
        this.getComponentName(controller) ||
        this.translate('AR.CONDITIONS_NL.BUTTON').toLowerCase();

      if (processorName && processorName.toLowerCase() == 'onoffclient') {
        let when = this.translate('GENERAL.WHEN');
        when = when.charAt(0).toUpperCase() + when.slice(1);
        return (
          when +
          ` ${deviceName} ${componentName} ${this.translate(
            'AR.CONNECTORS.IS_PRESSED'
          )}`
        );
      }

      return desc;
    }

    return desc.charAt(0).toUpperCase() + desc.substr(1);
  }

  public getActionDescription(
    conditions: [ConditionModel],
    effects: [EffectModel]
  ) {
    const description: any = { conditions: [], effects: [] };

    for (let i = 0; i < conditions.length; i++) {
      description.conditions.push(conditions[i].description);
    }

    for (let i = 0; i < effects.length; i++) {
      description.effects.push(effects[i].description);
    }

    return description;
  }

  private extractMethodFromPath(path: any, resName: any) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    if (path) {
      const split = path.split('/');
      if (split.length >= 8 && split[1] == resName && split[7] == 'methods') {
        return split[8];
      }
    }

    return null;
  }

  private getProcessorFromPath(path: any, resName: any) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    if (path) {
      const split = path.split('/');
      if (split.length >= 7 && split[1] == resName && split[5] == 'procs') {
        return split[6];
      }
    }

    return null;
  }

  private getComponentFromPath(path: any, resName: any) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    if (path) {
      const split = path.split('/');
      if (split.length >= 8 && split[1] == resName && split[3] == 'comps') {
        return split[4];
      }
    }

    return null;
  }

  private getAttributeFromPath(path: any, resName: any) {
    // /devices/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    if (path) {
      const split = path.split('/');
      if (split.length >= 8 && split[1] == resName && split[7] == 'attrs') {
        return split[8];
      }
    }

    return null;
  }

  private getResourceNameFromPath(path: any) {
    if (path) {
      const split = path.split('/');
      if (split.length > 2) {
        return split[1];
      }
    }

    return null;
  }

  private getResourceNameFromParents(resource: any, resName: any) {
    if (
      resource.path &&
      resource.parents &&
      Array.isArray(resource.parents[resName])
    ) {
      const resId = this.getResourceIdFromPath(resource.path, resName);

      if (resId) {
        for (let i = 0; i < resource.parents[resName].length; i++) {
          const parent = resource.parents[resName][i];

          if (parent.id.toString() == resId) {
            return parent.name;
          }
        }
      }
    }

    return null;
  }

  private getInputDeviceName(device: any, input: any) {
    const deviceId = device && device._id ? device._id : '';
    let deviceName = device && device.name
      ? device.name
      : `${this.translate('GENERAL.DEVICE')}`;
    deviceName = device && !device.name && deviceId ? deviceName + ' ' + deviceId : deviceName;
    const inputName = _.get(input, 'name', '');
    return inputName ? deviceName + ' ' + inputName : deviceName;
  }

  private getResourceIdFromPath(path: any, resName: any) {
    // /areas/582a1bddad04460fe3ac527a/comps/1/procs/OnOff/methods/On

    if (path) {
      const split = path.split('/');
      if (split.length > 2 && split[1] == resName) {
        return split[2];
      }
    }

    return null;
  }

  private getColorFromColorControlPayload(method: any, value: any) {
    let plugin: IColorControlServerMode;
    if (method.indexOf('movetocolortemperature') === 0) {
      plugin = (new ColorControlServerTemperatureMode());
    } else if (method.indexOf('movetohueandsaturation') === 0) {
      plugin = (new ColorControlServerHueSaturationMode());
    } else if (method.indexOf('movetocolor') === 0) {
      plugin = (new ColorControlServerXYMode());
    } else {
      return '';
    }

    const color = plugin.getEffectColorName(value).toLowerCase();
    return color;
  }
}
